import React, {useState} from 'react';
import {Alert, Button, Card, Col, InputNumber, message, Radio, Row, Space, Tag, TimePicker, Input} from "antd";
import {ClockCircleOutlined, CodeOutlined, SendOutlined, TeamOutlined, UserOutlined} from "@ant-design/icons";
import TextArea from "antd/lib/input/TextArea";
import ApiHandleSuccessMessage, {ApiHandleErrorMessage} from "@/lib/helper/apiHandleResultMessage";
import {useApiPutAdminProductDeliveryStepNotif} from "@/api/product";
import dayjs from 'dayjs';
import RenderIf from "@/components/render-if";

function AddNewSmsProductDeliveryStep({id, refetch, data}) {
    const [smsText, setSmsText] = useState('');
    const [startTime, setStartTime] = useState(dayjs().hour(8).minute(0));
    const [endTime, setEndTime] = useState(dayjs().hour(18).minute(0));
    const [intervalMinutes, setIntervalMinutes] = useState(15);
    const [recipientCount, setRecipientCount] = useState(1);
    // 👇 state جدید
    const [notifTo, setNotifTo] = useState(data?.data?.data?.meta?.deliver_to_lesson ? 'lesson' : 'user');

    const {mutate: sendNotif, isPending: isPendingSendNotif} = useApiPutAdminProductDeliveryStepNotif()

    const availableVariables = [
        { key: ':gender:', label: 'جنسیت', example: 'آقا/خانم', icon: <UserOutlined /> },
        { key: ':firstname:', label: 'نام', example: 'علی' },
        { key: ':lastname:', label: 'نام خانوادگی', example: 'علوی' },
        { key: ':code:', label: 'کد تحویل', example: '12345' },
        { key: ':time:', label: 'زمان نوبت', example: '08:15' },
    ];

    const insertVariable = (variable) => {
        const textarea = document.getElementById('sms-textarea');
        if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const text = smsText;
            const newText = text.substring(0, start) + variable + text.substring(end);
            setSmsText(newText);

            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start + variable.length, start + variable.length);
            }, 10);
        } else {
            setSmsText(prev => prev + variable);
        }
    };

    const getPreviewText = () => {
        const sampleData = {
            ':gender:': 'آقای',
            ':firstname:': 'علی',
            ':lastname:': 'علوی',
            ':code:': '12345',
            ':time:': startTime.format('HH:mm'),
        };

        let preview = smsText;
        Object.keys(sampleData).forEach(key => {
            preview = preview.replaceAll(key, sampleData[key]);
        });
        return preview;
    };

    const handleSendSms = async () => {
        if (!smsText.trim()) {
            message.warning('لطفاً متن پیامک را وارد کنید');
            return;
        }

        const payload = {
            id: id,
            notif_text: smsText,
            start_time: startTime.format('HH:mm'),
            end_time: endTime.format('HH:mm'),
            interval_minutes: intervalMinutes,
            recipient_count: recipientCount,
            notif_to: notifTo,
        };

        sendNotif(payload, {
            onSuccess: (r) => {
                ApiHandleSuccessMessage(r);
                refetch();
                setSmsText('');
            },
            onError: (e) => {
                ApiHandleErrorMessage(e);
            }
        });
    };


    return (
        <div className="space-y-6">

            <RenderIf condition={data?.data?.data?.meta?.deliver_to_lesson}>
            {/* 👇 کارت انتخاب مخاطب */}
            <Card size="small" title={
                <Space>
                    <TeamOutlined />
                    <span>ارسال پیامک به</span>
                </Space>
            }>
                <Radio.Group
                    value={notifTo}
                    onChange={(e) => setNotifTo(e.target.value)}
                    disabled={isPendingSendNotif}
                    optionType="button"
                    buttonStyle="solid"
                >
                    <Radio.Button value="user">
                        <Space>
                            <UserOutlined />
                            کاربران
                        </Space>
                    </Radio.Button>
                    <Radio.Button value="lesson">
                        <Space>
                            <TeamOutlined />
                            مربیان
                        </Space>
                    </Radio.Button>
                </Radio.Group>
            </Card>
            </RenderIf>

            <Card size="small" title={
                <Space>
                    <CodeOutlined />
                    <span>متغیرهای قابل استفاده در پیامک</span>
                </Space>
            }>
                <div className="flex flex-wrap gap-2">
                    {availableVariables.map((variable) => (
                        <Tag
                            key={variable.key}
                            color="blue"
                            className="cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => insertVariable(variable.key)}
                            icon={variable.icon}
                        >
                            {variable.key} - {variable.label}
                        </Tag>
                    ))}
                </div>
                <div className="text-xs   mt-2">
                    💡 برای درج متغیر، روی هر کدام کلیک کنید
                </div>
            </Card>

            <Card size="small" title={
                <Space>
                    <ClockCircleOutlined />
                    <span>تنظیمات زمان‌بندی ارسال</span>
                </Space>
            }>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={6}>
                        <div className="font-medium mb-1">ساعت شروع</div>
                        <TimePicker
                            value={startTime}
                            onChange={setStartTime}
                            format="HH:mm"
                            className="w-full"
                            placeholder="ساعت شروع را انتخاب کنید"
                            disabled={isPendingSendNotif}
                        />
                    </Col>
                    <Col xs={24} sm={6}>
                        <div className="font-medium mb-1">ساعت پایان</div>
                        <TimePicker
                            value={endTime}
                            onChange={setEndTime}
                            format="HH:mm"
                            className="w-full"
                            placeholder="ساعت پایان را انتخاب کنید"
                            disabled={isPendingSendNotif}
                        />
                    </Col>
                    <Col xs={24} sm={6}>
                        <div className="font-medium mb-1">فاصله زمانی (دقیقه)</div>
                        <InputNumber
                            min={1}
                            max={120}
                            value={intervalMinutes}
                            onChange={setIntervalMinutes}
                            className="w-full"
                            placeholder="مثلاً 15"
                            disabled={isPendingSendNotif}
                            addonAfter="دقیقه"
                        />
                    </Col>
                    <Col xs={24} sm={6}>
                        <div className="font-medium mb-1">تعداد دریافت‌کننده در هر نوبت</div>
                        <InputNumber
                            min={1}
                            max={100}
                            value={recipientCount}
                            onChange={setRecipientCount}
                            className="w-full"
                            placeholder="تعداد نفرات"
                            disabled={isPendingSendNotif}
                            addonAfter="نفر"
                        />
                    </Col>
                </Row>

                <div className="mt-3 text-sm   bg-blue-50 p-2 rounded">
                    <div className="flex flex-wrap gap-2">
                        <ClockCircleOutlined className="mr-1" />
                        مثال:
                        با تنظیمات فعلی،
                        اولین نوبت ساعت
                        <span className={'font-bold'}>{startTime.format('HH:mm')}</span>
                        تنظیم می‌شود و
                        تا ساعت
                        <span className={'font-bold'}>{endTime.format('HH:mm')}</span>
                        هر
                        <span className={'font-bold'}>{intervalMinutes}</span>
                        دقیقه،
                        <span className={'font-bold'}>{recipientCount}</span>
                        نفر بعدی نوبت دریافت می‌کنند.
                    </div>
                </div>
            </Card>

            <div className="space-y-4">
                <Input.TextArea
                    id="sms-textarea"
                    rows={4}
                    value={smsText}
                    onChange={(e) => setSmsText(e.target.value)}
                    placeholder={'متن پیامک را وارد کنید...'}
                    maxLength={500}
                    showCount
                    disabled={isPendingSendNotif}
                />

                {smsText && (
                    <Alert
                        message="پیش‌نمایش پیامک"
                        description={
                            <div className="whitespace-pre-wrap  p-2 rounded border">
                                {getPreviewText()}
                            </div>
                        }
                        type="info"
                        showIcon
                    />
                )}
                <span>پیامک فقط به افرادی که وضعیت تحویل آنها « درانتظار» است  ارسال خواهد شد</span>
                <div className="flex pt-2 justify-end">
                    <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={handleSendSms}
                        loading={isPendingSendNotif}
                        size="large"
                    >
                        ارسال پیامک
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default AddNewSmsProductDeliveryStep;